export type PlatformWebEnvironment = "development" | "test" | "staging" | "production";

export interface PlatformWebConfig {
  environment: PlatformWebEnvironment;
  expectedOrigin: string;
  apiBasePath: "/api/v1/platform";
}

interface BrowserEnvironment {
  VITE_CLICKDIALOG_PLATFORM_ENVIRONMENT?: string;
  VITE_CLICKDIALOG_PLATFORM_EXPECTED_ORIGIN?: string;
  VITE_CLICKDIALOG_PLATFORM_API_BASE_PATH?: string;
}

export function parsePlatformWebConfig(
  values: BrowserEnvironment,
  currentOrigin: string,
): PlatformWebConfig {
  const environment = values.VITE_CLICKDIALOG_PLATFORM_ENVIRONMENT ?? "development";
  if (!["development", "test", "staging", "production"].includes(environment)) {
    throw new Error("platform_web_environment_invalid");
  }
  const apiBasePath = values.VITE_CLICKDIALOG_PLATFORM_API_BASE_PATH ?? "/api/v1/platform";
  if (apiBasePath !== "/api/v1/platform") throw new Error("platform_web_api_must_be_same_origin");
  const expectedOrigin = values.VITE_CLICKDIALOG_PLATFORM_EXPECTED_ORIGIN ?? currentOrigin;
  let parsed: URL;
  try { parsed = new URL(expectedOrigin); } catch { throw new Error("platform_web_origin_invalid"); }
  if (parsed.origin !== expectedOrigin || parsed.pathname !== "/" || parsed.search || parsed.hash) throw new Error("platform_web_origin_invalid");
  if (parsed.hostname === "go.clickdialog.com") throw new Error("platform_web_public_origin_forbidden");
  if (environment === "production" && expectedOrigin !== "https://app.clickdialog.com") throw new Error("platform_web_production_origin_invalid");
  if (environment === "staging" && expectedOrigin !== "https://app.staging.clickdialog.com") throw new Error("platform_web_staging_origin_invalid");
  if ((environment === "staging" || environment === "production") && parsed.protocol !== "https:") throw new Error("platform_web_deployed_https_required");
  if ((environment === "staging" || environment === "production") && currentOrigin !== expectedOrigin) throw new Error("platform_web_runtime_origin_mismatch");
  return { environment: environment as PlatformWebEnvironment, expectedOrigin, apiBasePath };
}

export function defaultPlatformWebEnvironment(
  currentOrigin: string,
  productionBuild: boolean,
): PlatformWebEnvironment {
  if (currentOrigin === "https://app.staging.clickdialog.com") return "staging";
  return productionBuild ? "production" : "development";
}

export function loadPlatformWebConfig(): PlatformWebConfig {
  const values = import.meta.env as unknown as BrowserEnvironment;
  return parsePlatformWebConfig(
    {
      ...values,
      VITE_CLICKDIALOG_PLATFORM_ENVIRONMENT:
        values.VITE_CLICKDIALOG_PLATFORM_ENVIRONMENT ??
        defaultPlatformWebEnvironment(window.location.origin, import.meta.env.PROD),
    },
    window.location.origin,
  );
}
